/**
 * FolderSidebar Component
 *
 * Sidebar component for folder navigation in the document dashboard.
 * Contains folder tree, create folder button, and "All Documents" option.
 * Supports responsive design with hamburger menu for mobile viewports.
 *
 * @module features/dashboard/components/FolderSidebar
 * @see SPEC-016 Requirement 3.6, 10.1
 */

import React, { useCallback, useState, useEffect } from 'react';
import { cn } from '../../../lib/cn';
import { FolderTree } from './FolderTree';
import { useTypedTranslation } from '../../../i18n';
import type { FolderNode } from '@linkode/shared';

// ============================================================================
// Constants
// ============================================================================

/** Breakpoint for mobile view (matches Tailwind's md breakpoint) */
const MOBILE_BREAKPOINT = 768;

// ============================================================================
// Types
// ============================================================================

export interface FolderSidebarProps {
  /** Folder tree data */
  folderTree: FolderNode[];
  /** Currently selected folder ID (null for "All Documents") */
  selectedFolderId: string | null;
  /** Folder selection handler */
  onFolderSelect: (folderId: string | null) => void;
  /** Folder expansion toggle handler */
  onFolderToggle: (folderId: string) => void;
  /** Create folder handler */
  onCreateFolder: () => void;
  /** Folder context menu handler */
  onFolderContextMenu?: (folder: FolderNode, event: React.MouseEvent) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Whether sidebar is open on mobile */
  isMobileOpen?: boolean;
  /** Toggle mobile sidebar handler */
  onMobileToggle?: () => void;
  /** Additional class names */
  className?: string;
}

// ============================================================================
// Icons
// ============================================================================

const AllDocsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={cn('w-5 h-5', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
  </svg>
);

const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={cn('w-4 h-4', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const MenuIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={cn('w-5 h-5', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={cn('w-5 h-5', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// ============================================================================
// Component
// ============================================================================

/**
 * FolderSidebar Component
 *
 * Sidebar for folder navigation with:
 * - "All Documents" option at the top
 * - Folder tree with expand/collapse
 * - Create folder button
 * - Responsive hamburger menu for mobile (< 768px)
 *
 * @example
 * ```tsx
 * <FolderSidebar
 *   folderTree={folders}
 *   selectedFolderId={selectedId}
 *   onFolderSelect={(id) => selectFolder(id)}
 *   onFolderToggle={(id) => toggleExpansion(id)}
 *   onCreateFolder={() => openCreateFolderDialog()}
 *   isMobileOpen={isSidebarOpen}
 *   onMobileToggle={() => setIsSidebarOpen(!isSidebarOpen)}
 * />
 * ```
 *
 * @see Requirement 3.6, 10.1
 */
export const FolderSidebar: React.FC<FolderSidebarProps> = ({
  folderTree,
  selectedFolderId,
  onFolderSelect,
  onFolderToggle,
  onCreateFolder,
  onFolderContextMenu,
  isLoading = false,
  isMobileOpen = false,
  onMobileToggle,
  className,
}) => {
  const { t } = useTypedTranslation();
  const [isMobile, setIsMobile] = useState(false);

  // Track viewport width for responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    // Initial check
    checkMobile();

    // Listen for resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleAllDocsKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onFolderSelect(null);
      }
    },
    [onFolderSelect]
  );

  // Handle folder selection with auto-close on mobile
  const handleFolderSelectWithClose = useCallback(
    (folderId: string | null) => {
      onFolderSelect(folderId);
      // Auto-close sidebar on mobile after selection
      if (isMobile && onMobileToggle) {
        onMobileToggle();
      }
    },
    [onFolderSelect, isMobile, onMobileToggle]
  );

  // Sidebar content (shared between desktop and mobile)
  const sidebarContent = (
    <aside
      className={cn(
        'flex flex-col',
        'w-64 h-full',
        'bg-white',
        'border-r-2 border-linkode-text',
        'overflow-hidden',
        className
      )}
      role="navigation"
      aria-label="Folder navigation"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b-2 border-linkode-text">
        <h2 className="font-mono font-bold text-linkode-text uppercase text-sm">
          {t('dashboard.sidebar.folders')}
        </h2>

        {/* Mobile Close Button */}
        {isMobile && onMobileToggle && (
          <button
            type="button"
            className={cn(
              'p-1 rounded-linkode',
              'text-linkode-text/50 hover:text-linkode-text',
              'hover:bg-linkode-bg-light',
              'focus:outline-none focus:ring-2 focus:ring-linkode-teal'
            )}
            onClick={onMobileToggle}
            aria-label={t('dashboard.sidebar.closeSidebar')}
          >
            <CloseIcon />
          </button>
        )}
      </div>

      {/* All Documents Option */}
      <div className="px-2 py-2">
        <div
          className={cn(
            'flex items-center gap-2 px-2 py-2 rounded-linkode',
            'cursor-pointer select-none',
            'transition-colors duration-150',
            selectedFolderId === null
              ? 'bg-linkode-bg-orange text-linkode-text'
              : 'hover:bg-linkode-bg-light text-linkode-text/80 hover:text-linkode-text'
          )}
          onClick={() => handleFolderSelectWithClose(null)}
          onKeyDown={handleAllDocsKeyDown}
          tabIndex={0}
          role="button"
          aria-selected={selectedFolderId === null}
        >
          <AllDocsIcon
            className={cn(
              'flex-shrink-0',
              selectedFolderId === null ? 'text-linkode-primary' : 'text-linkode-text/60'
            )}
          />
          <span className="font-mono text-sm font-medium">
            {t('dashboard.sidebar.allDocuments')}
          </span>
        </div>
      </div>

      {/* Folder Tree */}
      <div className="flex-1 overflow-y-auto px-2 py-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-linkode-text border-t-transparent" />
          </div>
        ) : folderTree.length > 0 ? (
          <FolderTree
            nodes={folderTree}
            selectedFolderId={selectedFolderId}
            onFolderSelect={handleFolderSelectWithClose}
            onFolderToggle={onFolderToggle}
            onFolderContextMenu={onFolderContextMenu}
          />
        ) : (
          <div className="text-center py-8 px-4">
            <p className="text-sm text-linkode-text/50 font-mono">
              {t('dashboard.sidebar.noFolders')}
            </p>
          </div>
        )}
      </div>

      {/* Create Folder Button */}
      <div className="px-3 py-3 border-t border-linkode-text/20">
        <button
          type="button"
          className={cn(
            'w-full flex items-center justify-center gap-2',
            'px-3 py-2 rounded-linkode',
            'border-2 border-dashed border-linkode-text/30',
            'text-linkode-text/60 font-mono text-sm',
            'hover:border-linkode-text hover:text-linkode-text',
            'hover:bg-linkode-bg-light',
            'focus:outline-none focus:ring-2 focus:ring-linkode-teal',
            'transition-colors duration-150'
          )}
          onClick={onCreateFolder}
          aria-label={t('dashboard.sidebar.newFolder')}
        >
          <PlusIcon />
          <span>{t('dashboard.sidebar.newFolder')}</span>
        </button>
      </div>
    </aside>
  );

  // Desktop: render sidebar directly
  // Mobile: render with overlay and slide-in animation
  if (!isMobile) {
    return sidebarContent;
  }

  // Mobile: hamburger button when closed, overlay + sidebar when open
  return (
    <>
      {/* Hamburger Menu Button (visible when sidebar is closed on mobile) */}
      {!isMobileOpen && (
        <button
          type="button"
          className={cn(
            'fixed top-4 left-4 z-50',
            'p-2 rounded-linkode',
            'bg-white border-2 border-linkode-text',
            'text-linkode-text',
            'hover:bg-linkode-bg-light',
            'focus:outline-none focus:ring-2 focus:ring-linkode-teal',
            'shadow-linkode'
          )}
          onClick={onMobileToggle}
          aria-label={t('dashboard.sidebar.openSidebar')}
        >
          <MenuIcon />
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className={cn(
            'fixed inset-0 bg-black/50 z-40',
            'transition-opacity duration-300',
            isMobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
          onClick={onMobileToggle}
          aria-hidden="true"
        />
      )}

      {/* Mobile Sidebar with slide-in animation */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50',
          'transform transition-transform duration-300 ease-in-out',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {sidebarContent}
      </div>
    </>
  );
};

export default FolderSidebar;
